import { Almanac } from "protos/robot_syncer/plant_profile";
import {
  type AlmanacGroup,
  useAlmanacOptions,
} from "portal/components/categoryTemplates/useAlmanacOptions";
import {
  categoryHasAlmanac,
  type CategoryOption,
  MOST_SIMILAR_TO_OPTIONS,
} from "portal/utils/categoryCollection/category";
import { FormikConfig } from "formik";
import { isValidAlmanac } from "portal/utils/shootTime";
import { object, string } from "yup";
import { resolveAlmanacCategory } from "../resolveAlmanacCategory";
import { useCreateCategoryTemplate } from "../useCreateCategoryTemplate";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export type AddExistingCategoryFormValues = {
  sourceCategory: CategoryOption | null;
  sourceAlmanacId: string;
  mostSimilarTo: string;
};

export type UseAddExistingCategoryFormResult = {
  almanacGroups: AlmanacGroup[];
  defaultAlmanacId: string;
  formConfig: FormikConfig<AddExistingCategoryFormValues>;
  isLoading: boolean;
};

export const useAddExistingCategoryForm = (
  customerUuid: string | undefined,
  onSubmitSuccess: (newCategoryId: string) => void
): UseAddExistingCategoryFormResult => {
  const { t } = useTranslation();
  const { almanacGroups, defaultAlmanacId, isLoading } =
    useAlmanacOptions(customerUuid);

  const createCategoryTemplate = useCreateCategoryTemplate(customerUuid);

  const initialValues: AddExistingCategoryFormValues = useMemo(
    () => ({
      // eslint-disable-next-line unicorn/no-null
      sourceCategory: null,
      sourceAlmanacId: defaultAlmanacId,
      mostSimilarTo: MOST_SIMILAR_TO_OPTIONS[0],
    }),
    [defaultAlmanacId]
  );

  const needsAlmanacSelectionValidation = (
    sourceCategory: CategoryOption | null
  ): boolean => sourceCategory !== null && !categoryHasAlmanac(sourceCategory);

  return {
    almanacGroups,
    defaultAlmanacId,
    isLoading,
    formConfig: {
      initialValues,
      validationSchema: object({
        sourceCategory: object().nullable().required(t("utils.form.required")),
        mostSimilarTo: string().when("sourceCategory", {
          is: needsAlmanacSelectionValidation,
          // eslint-disable-next-line unicorn/no-thenable
          then: (schema) => schema.required(t("utils.form.required")),
          otherwise: (schema) => schema,
        }),
        sourceAlmanacId: string().when("sourceCategory", {
          is: needsAlmanacSelectionValidation,
          // eslint-disable-next-line unicorn/no-thenable
          then: (schema) => schema.required(t("utils.form.required")),
          otherwise: (schema) => schema,
        }),
      }),
      onSubmit: async (
        { sourceCategory, mostSimilarTo, sourceAlmanacId },
        { setFieldError, setFieldTouched }
      ) => {
        if (!sourceCategory) {
          return;
        }

        const needsAlmanacSelection = !categoryHasAlmanac(sourceCategory);
        let almanac: Almanac;
        if (needsAlmanacSelection) {
          const resolved = resolveAlmanacCategory(
            almanacGroups,
            sourceAlmanacId,
            mostSimilarTo,
            t
          );
          if (!resolved.ok) {
            setFieldError(resolved.field, resolved.message);
            return;
          }
          almanac = resolved.almanac;
        } else {
          const sourceAlmanac = sourceCategory.almanac;
          if (!isValidAlmanac(sourceAlmanac)) {
            await setFieldTouched("sourceCategory", true);
            setFieldError(
              "sourceCategory",
              t("components.categoryTemplates.errors.missingSourceAlmanac")
            );
            return;
          }
          almanac = sourceAlmanac;
        }

        const createdId = await createCategoryTemplate({
          name: sourceCategory.name,
          chipIds: sourceCategory.chipIds,
          almanac,
        });
        if (createdId) {
          onSubmitSuccess(createdId);
        }
      },
    },
  };
};
